import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Grid, Box, Button, MenuItem, Stack } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment-timezone";

import MpgInput from "../../../../CommonFormComp/MpgInput";
import MpgSelect from "../../../../CommonFormComp/MpgSelect";
import MpgItem from "../../../../CommonFormComp/MpgItem";
import {
  useGetMeetingStatusListQuery,
  useUpdateMeetingRequestIdMutation,
} from "../../../../reactQueries/AppApi/rqHooksForMeetingRequests";
import { meetingReqViewPath } from "../../../../routes/routePaths";
import MpgErrorDialog from "../../../../common/dialog/MpgErrorDialog";
import MpgLoadingDialog from "../../../../common/dialog/MpgLoadingDialog";
import { useGetSiteCoreCMSData } from "../../../../reactQueries/commonRQHooks";


const EditMeetingRequest = ({ meetingDataById }) => {
  const navigate = useNavigate();
  // fetch meeting status dropdown values
  const { data: statusDropdownData } = useGetMeetingStatusListQuery();

  /**API call to update meeting status and msg */
  const {
    mutate: updateMeetingRequest,
    isError: isErrorOnUpdate,
    isLoading: isLoadingOnUpdate,
    isSuccess: isSuccessOnUpdate,
    data: dataOnUpdate,
    error: errorOnUpdate,
  } = useUpdateMeetingRequestIdMutation();

  /**form handling */
  const schema = yup.object().shape({
    status: yup.string().label('This field').required(),
    admin_notes: yup.string().label('This field').required().max(1300),
  });

  const defaultValues = {
    meeting_title: meetingDataById?.Title,
    client_acc: meetingDataById?.ClientAccountName,
    client_sub_acc: meetingDataById?.ClientSubAccountName,
    service_office: meetingDataById?.ClientService,
    length: meetingDataById?.Duration,
    status: meetingDataById?.StatusId,
    requested_for: meetingDataById?.RequestedFor,
    requested_by: meetingDataById?.Requestedby,
    requested_on: meetingDataById?.requestedOn,
    submitted_on: meetingDataById?.submittedOn,
    admin_notes: meetingDataById?.AdminNotes || "",
    timezone: meetingDataById?.TimeZone,
    request_type: meetingDataById?.Location,
    request_available_slot: `${moment(meetingDataById?.MeetingDate).format(
      "DD MMM YYYY"
    )} - ${meetingDataById?.PreferredTimeSlot}`,
    notes: meetingDataById?.Notes,
  };
  const rhfFormInstance = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const onSubmit = (data) => {
    // console.log("formData=", data);
    const Id = meetingDataById?.Id;
    const body = {
      Id: Id,
      AdminNotes: data?.admin_notes,
      StatusId: data?.status,
    };
    // console.log("body=", body);
    updateMeetingRequest({ body, Id });
  };

  const {isSuccess: isSuccessOnSiteCoreCMS, error: errorOnSiteCoreCMS, data: adminMeetingReqCMSDataFromAPI, isError: isErrorOnSiteCoreCMS, isFetching: isFetchingOnSiteCoreCMS} = useGetSiteCoreCMSData({pagename: "MeetingRequests"});

  const [meetingReqCMSData, setMeetingReqCMSData] = useState({
    title: "",
    description: "",
    tabNavigationTitle: "",
    pageContent: {},
  });

  useEffect(() => {
    if (!!isSuccessOnUpdate) {
      navigate(meetingReqViewPath);
    }
  }, [isSuccessOnUpdate]);

  const fetchMeetingReqCMSData = () =>{
    if(adminMeetingReqCMSDataFromAPI && adminMeetingReqCMSDataFromAPI.Pages){
      adminMeetingReqCMSDataFromAPI.Pages.map((el) => {
       if (el.Key === "MeetingRequests") {
         const { Title, Description, TabNavigationTitle, Components } = el;
         setMeetingReqCMSData({ title: Title, description: Description, tabNavigationTitle: TabNavigationTitle, pageContent: Components });
       }
   });
    }
  }

  const fetchMeetingReqDtlsLabel = (key) => {
    if (meetingReqCMSData && meetingReqCMSData?.pageContent["MeetingRequestsDetailsComponent"]) {
        const meetingReqCMSDataLabel = meetingReqCMSData.pageContent["MeetingRequestsDetailsComponent"];
        return returnLabel(meetingReqCMSDataLabel?.Labels, key)
    }
    return '';
  }

  const fetchMeetingReqListLabel = (key) => {
    if (meetingReqCMSData && meetingReqCMSData?.pageContent["MeetingRequestsListComponent"]) {
        const meetingReqCMSDataLabel = meetingReqCMSData.pageContent["MeetingRequestsListComponent"];
        return returnLabel(meetingReqCMSDataLabel?.Labels, key)
    }
    return '';
  }

  const fetchCommonCMSLabel = (key) => {
    if (meetingReqCMSData && meetingReqCMSData?.pageContent["CommonComponent"]) {
        const meetingReqCMSDataLabel = meetingReqCMSData.pageContent["CommonComponent"];
        return returnLabel(meetingReqCMSDataLabel?.Labels, key)
    }
    return '';
  }

  const returnLabel = (labelArray=[], key) => {
        return labelArray.find((x) => x.Key === key)?.Phrase || '';
  }

  useEffect(() => {
    fetchMeetingReqCMSData();
   }, [adminMeetingReqCMSDataFromAPI]);

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
          {/* Edit Meeting Request */}
          {meetingReqCMSData?.pageContent["MeetingRequestsDetailsComponent"]?.Title}
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          m={1}
        >
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to={meetingReqViewPath}
          >
            {/* Meeting Requests */}
            {fetchMeetingReqDtlsLabel('MeetingRequestsDetails.MeetingRequests')}
          </Button>
        </Stack>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={rhfFormInstance.handleSubmit(onSubmit)}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <MpgItem>
                <Typography
                  variant="h6"
                  align="center"
                  sx={{ fontWeight: "bolder" }}
                >
                  Meeting Request Details
                </Typography>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"meeting_title"}
                  label={fetchMeetingReqListLabel("MeetingRequestsList.MeetingTitle") } //"Meeting Title"
                  fullWidth
                  disabled={true}
                />
                <MpgSelect
                  rhfFormInstance={rhfFormInstance}
                  name={"status"}
                  label={fetchMeetingReqListLabel("MeetingRequestsList.Status") } //"Status"}
                  fullWidth
                >
                  {!!statusDropdownData &&
                    statusDropdownData.map((eachItem) => (
                      <MenuItem
                        key={eachItem.MeetingId}
                        value={eachItem?.MeetingId}
                      >
                        {eachItem?.MeetingStatusDescription}
                      </MenuItem>
                    ))}
                </MpgSelect>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"admin_notes"}
                  label={fetchMeetingReqDtlsLabel("MeetingRequestsDetails.AdminNotes") } //{"Admin Notes"}
                  placeholder={fetchMeetingReqDtlsLabel("MeetingRequestsDetails.AddMessageHere") } //{"Add message here..."}
                  multiline
                  rows={4}
                  fullWidth
                />
              </MpgItem>
              <MpgItem sx={{ mt: 2 }}>
                <Typography
                  variant="h6"
                  align="center"
                  sx={{ fontWeight: "bolder" }}
                >
                  User Belongs to
                </Typography>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"client_acc"}
                  label={fetchMeetingReqListLabel("MeetingRequestsList.ClientAccount")} //{"Client Account"}
                  fullWidth
                  disabled={true}
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"client_sub_acc"}
                  label={fetchMeetingReqListLabel("MeetingRequestsList.ClientSubAccount")} //{"Client Sub-Account"}
                  fullWidth
                  disabled={true}
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"service_office"}
                  label={fetchMeetingReqDtlsLabel("MeetingRequestsDetails.ServiceOffice")} //{"Service Office"}
                  fullWidth
                  disabled={true}
                />
              </MpgItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MpgItem>
                <Typography
                  variant="h6"
                  align="center"
                  sx={{ fontWeight: "bolder" }}
                >
                  {/* Meeting Request Information */}
                  {fetchMeetingReqDtlsLabel("MeetingRequestsDetails.MeetingRequestInformation")}
                </Typography>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"requested_by"}
                  label={fetchMeetingReqDtlsLabel('MeetingRequestsDetails.RequestSubmittedBy')} //"Request Submitted By"
                  placeholder={fetchMeetingReqListLabel('MeetingRequestsList.RequestedBy')} //"Requested By"
                  fullWidth
                  disabled={true}
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"requested_for"}
                  label={fetchMeetingReqDtlsLabel('MeetingRequestsDetails.RequestedFor')} //"Requested For"
                  fullWidth
                  disabled={true}
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"length"}
                  label= {fetchMeetingReqDtlsLabel('MeetingRequestsDetails.Length')} //"Length"
                  fullWidth
                  disabled={true}
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"timezone"}
                  label={fetchMeetingReqDtlsLabel('MeetingRequestsDetails.RequestedTimezone')} //"Requested Timezone"
                  fullWidth
                  disabled={true}
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"request_type"}
                  label= {fetchMeetingReqDtlsLabel('MeetingRequestsDetails.RequestedType')} //"Requested Type"
                  fullWidth
                  disabled={true}
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"request_available_slot"}
                  label= {fetchMeetingReqDtlsLabel('MeetingRequestsDetails.RequestedAvailableTimeSlot')} //"Requested Available Time Slot"
                  fullWidth
                  disabled={true}
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"notes"}
                  label={fetchMeetingReqDtlsLabel('MeetingRequestsDetails.Notes')} //"Notes"
                  fullWidth
                  disabled={true}
                />
              </MpgItem>
            </Grid>
          </Grid>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={"space-evenly"}
            mt={3}
          >
            <Button variant="outlined" type="submit" size="small">
              {fetchCommonCMSLabel('Common.Submit')}
            </Button>
          </Stack>
        </Box>

        {/** error dialog on update action */}
        <MpgErrorDialog
          open={!!isErrorOnUpdate}
        />
        <MpgLoadingDialog
          open={
            !!isLoadingOnUpdate
          }
        />
      </Box>
    </>
  );
};

export default EditMeetingRequest;
